<template>
  <bean-source-page
    :type="type"
    :columns="columns"
    :form-columns="formColumns"
    :filter-columns="filterColumns"
    :actions="actions"
    :form="form"
    :title="$t('model.adminUsersRole')"
    resource="admin_users_roles"
    :onFetchData="onFetchData"
  />
</template>

<script>
import { Vue, Component, Prop } from 'vue-property-decorator';
import _ from 'lodash';
import { filterColumnsHash, columnsHash, formColumnsHash } from '../admin_configs/admin_users_roles/columns';

@Component
export default class AdminUsersRoles extends Vue {
  @Prop(String) type;

  // form 新建表单初始化数据（default数据）
  form = {}

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  filterColumns() {
    return [
      {
        prop: 'admin_user_email_cont',
        label: this.$t('adminUser.email'),
        renderCell: {
          component: 'input',
        }
      }
    ].filter(Boolean).map(hash => _.merge(filterColumnsHash[hash['prop']], hash))
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   */
  columns() {
    return [
      { prop: 'id' },
      {
        prop: 'admin_user.email',
        label: this.$t('adminUser.email'),
        minWidth: 140,
        renderCell: 'mail'
      },
      { prop: 'role_id' },
      {
        prop: 'admin_user.actived_at',
        label: this.$t('adminUser.activedAt'),
        minWidth: 110,
        renderCell: 'time'
      },
      { prop: 'created_at' },
      { prop: 'updated_at' }
    ].filter(Boolean).map(hash => _.merge(columnsHash[hash['prop']], hash))
  }

  /**
   * @param {Function} h - createElement
   * @param {Object} params
   * @param {String} params.type - 页面类型 enum: ['index', 'show', 'edit', 'new']
   * @param {String} params.resource -页面RESTful api资源名
   * @param {Object} params.data - 当前表单数据
   * @param {Function} params.change - 调用此方法同步表单数据 change({ field: 'new value' })
   */
  formColumns(h, { type }) {
    return [
      // 防止chrome自动填充账号密码
      {
        prop: 'email_placeholder',
        label: 'email',
        renderCell: (h) => <input h={h} />
      },
      {
        prop: 'admin_user.email',
        label: this.$t('adminUser.email'),
        renderCell: {
          component: 'input',
          required: true,
          props: {
            disabled: type === 'edit',
          }
        }
      },
      {
        prop: 'role_id',
        renderCell: {
          props: {
            xRemotePreload: async() => {
              const { data } = await this.$request.get('/roles', { params: { kind_eq: 'custom', per_page: 999 } });
              return data.map(item => ({ value: item.id, label: item.name }));
            },
          }
        }
      },
    ].filter(Boolean).map(hash => _.merge(formColumnsHash[hash['prop']], hash))
  }

   /**
   * @param {Object} scope -页面/表格 scope 数据
   * @param {Object} scope.row - 详情页/表格行 数据
   * @param {Object} sourcePageScope
   * @param {Function} sourcePageScope.remove - sourcePage删除方法
   */
  actions({ row }, { remove, reload }) {
    return [
      {
        component: 'routeButton',
        props: {
          label: this.$t('edit'),
          to: { name: 'admin_users_roles.edit', params: { id: row.id } }
        }
      },
      !_.get(row, 'admin_user.actived_at') && this.$root.user.id !== row.admin_user_id && {
        component: 'confirmButton',
        props: {
          label: this.$t('adminUser.actions.enable'),
          type: 'primary',
          can: 'admin_users_roles.enable_admin'
        },
        on: {
          confirm: async () => {
            await this.$request.post(`/admin_users_roles/${row.id}/enable`);
            reload();
          }
        }
      },
      _.get(row, 'admin_user.actived_at') && this.$root.user.id !== row.admin_user_id && {
        component: 'confirmButton',
        props: {
          label: this.$t('adminUser.actions.disable'),
          type: 'danger',
          can: 'admin_users_roles.disable_admin'
        },
        on: {
          confirm: async () => {
            await this.$request.post(`/admin_users_roles/${row.id}/disable`);
            reload();
          }
        }
      },
      {
        component: 'confirmButton',
        props: {
          label: this.$t('destroy'),
          type: 'danger',
          can: 'admin_users_roles.destroy'
        },
        on: {
          confirm: () => remove(row.id)
        }
      }
    ].filter(Boolean);
  }

  async onFetchData({ type }) {
    if (type === 'index') {
      const { data, pagination } = await this.$request.get('/admin_users_roles', { params: { ...this.$route.query, role_kind_eq: 'custom' } });
      return {
        data,
        pagination
      }
    }

    if (type === 'show' || type === 'edit') {
      const data = await this.$request.get(`/admin_users_roles/${this.$route.params.id}`);
      return { data };
    }

    if (type === 'new') {
      return {}
    }
  }
}

export const route = [
  {
    path: '/admin_users_roles',
    name: 'admin_users_roles.index',
    component: AdminUsersRoles,
    props: {
      type: 'index',
    }
  },
  {
    path: '/admin_users_roles/new',
    name: 'admin_users_roles.new',
    component: AdminUsersRoles,
    props: {
      type: 'new',
    }
  },
  {
    path: '/admin_users_roles/:id',
    name: 'admin_users_roles.show',
    component: AdminUsersRoles,
    props: {
      type: 'show',
    }
  },
  {
    path: '/admin_users_roles/:id/edit',
    name: 'admin_users_roles.edit',
    component: AdminUsersRoles,
    props: {
      type: 'edit'
    }
  }
]
</script>

<style lang="scss" scoped>
  /deep/ .email_placeholder, /deep/ .password_placeholder {
    position: fixed;
    top: -200%;
    left: -200%;
  }
</style>
